<template>
  <div class="col-12 col-md-6 bc-col">
    <div class="bc-card">
      <BudgetCardHeader :summary="summary" @update:scope="onScopeChange" @update:limit="onLimitChange" @update:headType="onHeadTypeChange"/>
      <div class="table-responsive btv-scroll">
        <table class="table table-bordered align-middle bc-table">
          <thead>
            <tr>
              <th class="text-start" :class="listType.head_class">Top {{ topLimit }} {{ listType.title }}</th>
              <th class="text-end btv-variance-head" :class="listType.head_class">{{ isItemHead ? 'Budget' : 'Variance' }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(row, index) in rows" :key="index">
              <td class="bc-name">
                <div class="btv-head-name">
                  <span class="btv-rank" :class="listType.rank_class">{{ index + 1 }}</span>
                  <span>{{ row.name }}</span>
                </div>
                <div class="btv-bar-track">
                  <div class="btv-bar" :class="listType.bar_class" :style="{ width: barWidth(row) }"></div>
                </div>
              </td>
              <td class="text-end bc-amount" :class="listType.amount_class">{{ commaFormat(rowAmount(row)) }}</td>
            </tr>
            <tr v-if="! rows.length">
              <td colspan="2" class="btv-empty">
                <span class="btv-empty-icon"><i class="fa-regular fa-folder-open"></i></span>
                <div>{{ emptyText }}</div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue'
import figureFormatter from '@/services/utils/figureFormatter'
import BudgetCardHeader from '@/components/molecule/company/dashboard/dynamic-dashboard/budget/BudgetCardHeader'
import { ITEM_HEAD, headDataOf } from '@/components/molecule/company/dashboard/dynamic-dashboard/budget/budgetFigures'

const ITEM_LIST_STYLE = {
  head_class: 'btv-head-budget',
  rank_class: 'btv-rank-budget',
  bar_class: 'btv-bar-budget',
  amount_class: '',
  empty_text: 'No Item budget to date'
}

const ITEM_LIST_TITLES = { favourable: 'Income', adverse: 'Expense' }

const LIST_TYPES = {
  favourable: {
    title: 'Favourable',
    head_class: 'btv-head-favourable',
    rank_class: 'btv-rank-favourable',
    bar_class: 'btv-bar-favourable',
    amount_class: 'bc-favourable',
    empty_text: 'No favourable line item'
  },
  adverse: {
    title: 'Adverse',
    head_class: 'btv-head-adverse',
    rank_class: 'btv-rank-adverse',
    bar_class: 'btv-bar-adverse',
    amount_class: 'bc-adverse',
    empty_text: 'No adverse line item'
  }
}

const props = defineProps({
  statisticsItem: Object,
  itemKey: String,
  page: Object
})

const { commaFormat } = figureFormatter()

const summary = computed(() => props.statisticsItem[props.itemKey].data || {})

const currentScope = ref(null)
const topLimit = ref(0)
const headType = ref(null)

const isItemHead = computed(() => headType.value === ITEM_HEAD)

const listType = computed(() => {
  const list = LIST_TYPES[summary.value.list_type] || LIST_TYPES.favourable
  if (! isItemHead.value) return list
  return { ...list, ...ITEM_LIST_STYLE, title: ITEM_LIST_TITLES[summary.value.list_type] }
})

const onScopeChange = (scope) => {
  currentScope.value = scope
}

const onLimitChange = (limit) => {
  topLimit.value = limit
}

const onHeadTypeChange = (value) => {
  headType.value = value
}

const rows = computed(() => {
  const head = headDataOf(currentScope.value, headType.value)
  if (! head) return []
  return (head.rows || []).slice(0, topLimit.value)
})

const emptyText = computed(() => (currentScope.value ? listType.value.empty_text : 'No approved budget'))

const rowAmount = (row) => (isItemHead.value ? row.budget : row.variance)

const barWidth = (row) => `${rowAmount(row) / rowAmount(rows.value[0]) * 100}%`
</script>

<style scoped src="./budget/budgetCard.css"></style>

<style scoped>
.btv-scroll {
  max-height: 340px;
  overflow-y: auto;
}
.bc-table thead th {
  position: sticky;
  top: 0;
  z-index: 1;
}
.bc-table thead th.btv-head-favourable {
  color: #0f766e;
  background-color: #e6f6f3 !important;
}
.bc-table thead th.btv-head-adverse {
  color: #c53030;
  background-color: #fdecec !important;
}
.bc-table thead th.btv-head-budget {
  color: #4338ca;
  background-color: #e0e7ff !important;
}
.btv-variance-head {
  width: 120px;
}
.btv-head-name {
  display: flex;
  align-items: flex-start;
  gap: 8px;
}
.btv-rank {
  flex: 0 0 20px;
  height: 20px;
  border-radius: 50%;
  font-size: 11px;
  font-weight: 700;
  line-height: 20px;
  text-align: center;
}
.btv-rank-favourable {
  color: #0f766e;
  background: #e6f6f3;
}
.btv-rank-adverse {
  color: #c53030;
  background: #fdecec;
}
.btv-rank-budget {
  color: #4338ca;
  background: #e0e7ff;
}
.btv-bar-track {
  height: 4px;
  margin: 6px 0 0 28px;
  border-radius: 2px;
  background: #f2f4f7;
}
.btv-bar {
  height: 100%;
  border-radius: 2px;
}
.btv-bar-favourable {
  background: #16a085;
}
.btv-bar-adverse {
  background: #ea5455;
}
.btv-bar-budget {
  background: #818cf8;
}
.btv-empty {
  padding: 36px 12px !important;
  text-align: center;
  font-size: 12px;
  font-weight: 400;
  color: #9a98a3;
}
.btv-empty-icon {
  display: block;
  margin-bottom: 6px;
  font-size: 20px;
  color: #c9ccd3;
}
</style>
